import { FileText, ShieldCheck } from 'lucide-react';
import type React from 'react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import terms from '@/../config/terms.json';
import { useAppContext } from '@/app/providers/AppContextProvider';
import Navigation from '../Navigation';
import TermCheck from '../TermCheck';
import Title from '../Title';

interface StepProps {
	step: number;
	setStep: (step: number) => void;
	errors: Record<string, string | undefined>;
	isStepValid: boolean;
	isSaving: boolean;
	setIsSaving: (saving: boolean) => void;
}

interface TermData {
	step: number;
	question: string;
	icon: string;
}

const iconMap: Record<string, React.ElementType> = {
	'fa-file-text': FileText,
	'fa-shield-halved': ShieldCheck
};

const Step3 = ({ step, setStep, errors, isStepValid, isSaving, setIsSaving }: StepProps) => {
	const { t } = useTranslation();
	const { applicationData, applicationManagement } = useAppContext();
	const [touched, setTouched] = useState<Record<string, boolean>>({});

	if (step !== 3) return null;

	const handleChange = (name: string, checked: boolean) => {
		setTouched((prev) => ({ ...prev, [name]: true }));
		applicationManagement.update({ [name]: checked });
	};

	const showError = (field: string) => touched[field] && errors?.[field];

	return (
		<div className="flex flex-col justify-center select-none">
			<div className="aw-full md:w-10/12 mx-auto">
				<div className="ml-2 sm:ml-0">
					<Title
						title={t('application.steps.step5.title')}
						subtitle={t('application.steps.step5.subtitle')}
					/>
					<p className="text-left sm:text-center leading-relaxed mb-9">
						{t('application.steps.step5.description')}
					</p>
				</div>

				{Object.entries(terms as Record<string, TermData>)
					.filter(([, termData]) => termData.step === 3)
					.map(([name, termData]) => {
						const IconComponent = iconMap[termData.icon] || FileText;
						const isChecked = Boolean(applicationData[name as keyof typeof applicationData]);

						return (
							<TermCheck
								key={name}
								name={name}
								label={t(`application.steps.step5.${name}`)}
								checked={isChecked}
								onCheckedChange={(checked) => handleChange(name, checked)}
								icon={IconComponent}
								error={showError(name) ? errors?.[name] : undefined}
							/>
						);
					})}
			</div>
			<Navigation
				step={step}
				setStep={setStep}
				nextButtonText={t('application.navigation.next')}
				nextButtonArrow={true}
				isStepValid={isStepValid}
				isSaving={isSaving}
				setIsSaving={setIsSaving}
			/>
		</div>
	);
};

export default Step3;
